// @flow

import React, {useEffect, useState} from 'react';
import {Box, Typography, makeStyles} from '@material-ui/core';
import {colors} from '../../../assets/styles/colors';
import CustomModal from '../../../elements/CustomModal/CustomModal';
import CustomButton from '../../../elements/CustomButton/CustomButton';
import CustomTextField from '../../../elements/CustomTextField/CustomTextField';
import InlineLoader from '../../../elements/LoadingSpinner/InlineLoader';
import {useStoreInfo} from '../../store/utils';
import type {Store} from '../../../entities';

const useStyles = makeStyles(theme => ({
  modalContent: {
    position: 'relative',
    width: '100%',
    minWidth: 0,
  },
  modalPaper: {
    minWidth: '300px',
    padding: '1rem',
    width: '80vw',
    maxWidth: '500px',
  },
  loader: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(255,255,255,0.6)',
    zIndex: 10,
    borderRadius: '12px',
  },
  headingRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '0.75rem',
  },
  subtitle: {
    fontSize: '1rem',
    fontWeight: 600,
    color: colors.grey800,
  },
  caption: {
    fontSize: '.875rem',
    color: colors.grey600,
  },
  stepContent: {
    marginTop: '0.75rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
    paddingRight: '0.25rem',
  },
  stepsFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '0.5rem',
    marginTop: '1rem',
    flexWrap: 'wrap',
  },
  botNameField: {
    marginBottom: '0.75rem',
  },
}));

type Props = {
  open: boolean,
  isSubmitting: boolean,
  onClose: () => void,
  onSubmit: ({name: string}) => void,
};

function getDefaultBotName(store?: Store): string {
  if (store?.name) {
    return `${store.name} Bot`;
  }
  return '';
}

export default function BotCreationModal(props: Props) {
  const {open, isSubmitting, onClose, onSubmit} = props;
  const classes = useStyles();
  const store = useStoreInfo();

  const [botName, setBotName] = useState(getDefaultBotName(store));

  useEffect(() => {
    if (!open) {
      return;
    }

    setBotName(getDefaultBotName(store));
  }, [open, store]);

  const trimmedBotName = botName.trim();
  const canSubmit = Boolean(trimmedBotName);

  const handleSubmit = () => {
    if (!canSubmit) {
      return;
    }
    onSubmit({name: trimmedBotName});
  };

  return (
    <CustomModal
      open={open}
      handleCloseModal={onClose}
      classes={{
        paper: classes.modalPaper,
        root: classes.rootModal,
      }}
    >
      <div className={classes.modalContent}>
        {isSubmitting && (
          <div className={classes.loader}>
            <InlineLoader loading size={36} />
          </div>
        )}

        <div className={classes.headingRow}>
          <Box>
            <Typography className={classes.subtitle}>
              Crear nuevo bot
            </Typography>
            <Typography className={classes.caption}>
              Configura tu asistente de manera simple.
            </Typography>
          </Box>
        </div>

        <div className={classes.stepContent}>
          <CustomTextField
            className={classes.botNameField}
            label="Nombre del bot"
            placeholder="Ej: Asistente de Mi Tienda"
            value={botName}
            onChange={event => setBotName(event.target.value)}
            disabled={isSubmitting}
          />
        </div>

        <div className={classes.stepsFooter}>
          <CustomButton
            variant="outlined"
            color="default"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancelar
          </CustomButton>

          <CustomButton
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={!canSubmit || isSubmitting}
          >
            Crear bot
          </CustomButton>
        </div>
      </div>
    </CustomModal>
  );
}
